import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import styles from './TaskModal.module.scss';
import api from '../../services/api';
import { getRolesFromStorage, subscribeToRoles } from '../../services/clientAuth';
import { clipboardPayloadToTableImageFile } from './okkClipboardImage';
import DesignLayoutPreview from '../DesignLayoutPreview/DesignLayoutPreview';

const TEAM_TS_ID = 1;
const TEAM_DESIGN_ID = 2;
const TEAM_TS_CODE = 'TS';
const TEAM_DESIGN_CODE = 'DESIGN';
const TEAM_OKK_CODE = 'OKK';
const ROLE_OKK = 'ROLE_OKK';

const CREATION_ROLE_OPTIONS_MAP = {
    ROLE_SPECIALIST: { value: 'ROLE_SPECIALIST', label: 'Специалист' },
    ROLE_SPECIALIST_TS: { value: 'ROLE_SPECIALIST_TS', label: 'Сотрудник Тех. Поддержки' },
    ROLE_OKK: { value: 'ROLE_OKK', label: 'ОКК' },
    ROLE_MANAGER: { value: 'ROLE_MANAGER', label: 'Менеджер' },
    ROLE_ADMIN: { value: 'ROLE_ADMIN', label: 'Администратор' },
    ROLE_SUPER_ADMIN: { value: 'ROLE_SUPER_ADMIN', label: 'Супер администратор' },
    ROLE_YK: { value: 'ROLE_YK', label: 'УК' },
    ROLE_EXTERNAL_CASHBOX: { value: 'ROLE_EXTERNAL_CASHBOX', label: 'Внешняя касса' },
};

const FIXED_TEAM_CODE_BY_ROLE = {
    ROLE_SPECIALIST: TEAM_DESIGN_CODE,
    ROLE_SPECIALIST_TS: TEAM_TS_CODE,
    ROLE_OKK: TEAM_OKK_CODE,
};

const FIXED_TEAM_LABEL_BY_CODE = {
    TS: 'IT ТП',
    DESIGN: 'Дизайн Чио Чио',
    OKK: 'ОКК',
};

const FIXED_TEAM_FALLBACK_OPTION_BY_CODE = {
    [TEAM_TS_CODE]: { value: TEAM_TS_ID, label: FIXED_TEAM_LABEL_BY_CODE[TEAM_TS_CODE], code: TEAM_TS_CODE },
    [TEAM_DESIGN_CODE]: { value: TEAM_DESIGN_ID, label: FIXED_TEAM_LABEL_BY_CODE[TEAM_DESIGN_CODE], code: TEAM_DESIGN_CODE },
};

const TASK_KIND_OPTIONS = [
    { value: 'common', label: 'Обычная задача' },
    { value: 'service_improvement', label: 'Доработка сервиса' },
];

Modal.setAppElement('#root');

function ensureNamedImageFile(file, fallbackPrefix = 'okk-image') {
    if (!file) {
        return null;
    }

    if (file.name && file.name !== '') {
        return file;
    }

    const extension = String(file.type || '').split('/')[1] || 'png';
    return new File([file], `${fallbackPrefix}-${Date.now()}.${extension}`, {
        type: file.type || 'image/png',
    });
}

function buildCreationRoleOptions(roles) {
    const uniqueRoles = Array.from(new Set(Array.isArray(roles) ? roles : []));

    return uniqueRoles
        .filter((role) => CREATION_ROLE_OPTIONS_MAP[role])
        .map((role) => CREATION_ROLE_OPTIONS_MAP[role]);
}

function buildUsersTaskUrl({ roleContext, teamCode }) {
    const params = new URLSearchParams();

    if (roleContext) {
        params.set('roleContext', roleContext);
    }

    if (teamCode) {
        params.set('teamCode', teamCode);
    }

    return `/api/usersTask${params.toString() ? `?${params.toString()}` : ''}`;
}

const TaskModal = ({ isOpen, onRequestClose, onCreateTask }) => {
    const [roles, setRoles] = useState(() => getRolesFromStorage());
    const [creationRole, setCreationRole] = useState(null);

    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [taskKind, setTaskKind] = useState(TASK_KIND_OPTIONS[0]);
    const [author, setAuthor] = useState(null);
    const [assignee, setAssignee] = useState(null);
    const [observers, setObservers] = useState([]);
    const [team, setTeam] = useState(null);
    const [priority, setPriority] = useState(null);
    const [terminalNumber, setTerminalNumber] = useState('');
    const [estimatedResolutionDate, setEstimatedResolutionDate] = useState('');

    const [usersOptionsAuthors, setUsersOptionsAuthors] = useState([]);
    const [usersOptionsObserver, setUsersOptionsObserver] = useState([]);
    const [usersOptionsPerformers, setUsersOptionsPerformers] = useState([]);
    const [usersOptionsPerformersOkk, setUsersOptionsPerformersOkk] = useState([]);
    const [teamsOptions, setTeamsOptions] = useState([]);
    const [isUploadingOkkImage, setIsUploadingOkkImage] = useState(false);

    const [itTpFields, setItTpFields] = useState({
        serviceType: '',
        address: '',
        comment: '',
        problemDescription: '',
        contactPhone: '',
        terminalPaymentBlocked: false,
    });

    const [designFields, setDesignFields] = useState({
        taskType: '',
        layoutRequired: '',
        layoutFormat: '',
        visualizationRequired: '',
        address: '',
        vkLink: '',
        comment: '',
        timeWork: '',
        linkRecord: '',
    });

    const [okkFields, setOkkFields] = useState({
        text: '',
        imageUrl: '',
        imageName: '',
    });

    const [errors, setErrors] = useState({});

    const creationRoleOptions = useMemo(() => buildCreationRoleOptions(roles), [roles]);
    const requiresCreationRoleChoice = creationRoleOptions.length > 1;
    const selectedCreationRoleValue = creationRole?.value || (creationRoleOptions.length === 1 ? creationRoleOptions[0].value : '');
    const shouldWaitForRoleSelection = requiresCreationRoleChoice && !selectedCreationRoleValue;
    const fixedTeamCode = selectedCreationRoleValue ? (FIXED_TEAM_CODE_BY_ROLE[selectedCreationRoleValue] || '') : '';

    const selectedTeamId = useMemo(() => Number(team?.value || 0), [team]);
    const selectedTeamCode = useMemo(() => String(team?.code || '').toUpperCase(), [team]);
    const effectiveTeamCode = fixedTeamCode || selectedTeamCode;
    const isItTp = effectiveTeamCode === TEAM_TS_CODE || (!fixedTeamCode && selectedTeamId === TEAM_TS_ID);
    const isDesign = effectiveTeamCode === TEAM_DESIGN_CODE || (!fixedTeamCode && selectedTeamId === TEAM_DESIGN_ID);
    const isOkk = effectiveTeamCode === TEAM_OKK_CODE;
    const shouldHideAuthor = isOkk && selectedCreationRoleValue === ROLE_OKK;
    const performerOptions = isOkk ? usersOptionsPerformersOkk : usersOptionsPerformers;

    const lockedTeamOption = useMemo(() => {
        if (!fixedTeamCode) {
            return null;
        }

        const fixedTeamLabel = FIXED_TEAM_LABEL_BY_CODE[fixedTeamCode] || '';

        return (
            teamsOptions.find((item) => String(item?.code || '').toUpperCase() === fixedTeamCode) ||
            teamsOptions.find((item) => String(item?.label || '').trim() === fixedTeamLabel) ||
            FIXED_TEAM_FALLBACK_OPTION_BY_CODE[fixedTeamCode] ||
            null
        );
    }, [teamsOptions, fixedTeamCode]);

    const descriptionObject = useMemo(() => {
        if (isItTp) return itTpFields;
        if (isDesign) return designFields;
        if (isOkk) return { kind: 'okk', text: okkFields.text, imageUrl: okkFields.imageUrl };
        return {};
    }, [isItTp, isDesign, isOkk, itTpFields, designFields, okkFields]);

    const descriptionJsonString = useMemo(() => JSON.stringify(descriptionObject), [descriptionObject]);

    useEffect(() => {
        setRoles(getRolesFromStorage());
        return subscribeToRoles(setRoles);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (creationRoleOptions.length === 1) {
            setCreationRole((prev) => (prev?.value === creationRoleOptions[0].value ? prev : creationRoleOptions[0]));
            return;
        }

        if (creationRole && creationRoleOptions.some((item) => item.value === creationRole.value)) {
            return;
        }

        setCreationRole(null);
    }, [isOpen, creationRoleOptions, creationRole]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (requiresCreationRoleChoice && !selectedCreationRoleValue) {
            setUsersOptionsAuthors([]);
            setUsersOptionsObserver([]);
            setUsersOptionsPerformers([]);
            setUsersOptionsPerformersOkk([]);
            setTeamsOptions([]);
            return;
        }

        const fetchData = async () => {
            try {
                const response = await api.get(buildUsersTaskUrl({
                    roleContext: selectedCreationRoleValue,
                    teamCode: effectiveTeamCode,
                }));

                const authors = Array.isArray(response.data?.Authors) ? response.data.Authors : [];
                const observersList = Array.isArray(response.data?.Observer) ? response.data.Observer : [];
                const performers = Array.isArray(response.data?.Performers) ? response.data.Performers : [];
                const teams = Array.isArray(response.data?.Teams) ? response.data.Teams : [];

                setUsersOptionsAuthors(authors);
                setUsersOptionsObserver(observersList);
                setUsersOptionsPerformers(performers);
                setUsersOptionsPerformersOkk(performers);
                setTeamsOptions(teams);
            } catch (error) {
                console.error('Ошибка при загрузке данных для модалки задач:', error);
            }
        };

        void fetchData();
    }, [isOpen, requiresCreationRoleChoice, selectedCreationRoleValue, effectiveTeamCode]);

    useEffect(() => {
        if (!fixedTeamCode || !lockedTeamOption) {
            return;
        }

        if (team?.value !== lockedTeamOption.value) {
            setTeam(lockedTeamOption);
            setAssignee(null);
        }
    }, [fixedTeamCode, lockedTeamOption, team]);

    useEffect(() => {
        if (!isOpen || !selectedCreationRoleValue) {
            return;
        }

        setAssignee(null);
        setErrors({});

        if (selectedCreationRoleValue === ROLE_OKK) {
            setAuthor(null);
        }

        if (!fixedTeamCode) {
            setTeam(null);
        }
    }, [isOpen, selectedCreationRoleValue, fixedTeamCode]);

    const priorityOptions = [
        { value: '4', label: 'Не важная' },
        { value: '2', label: 'Средняя' },
        { value: '3', label: 'Важная' },
        { value: '1', label: 'Критическая' },
    ];

    const resetForm = () => {
        setCreationRole(creationRoleOptions.length === 1 ? creationRoleOptions[0] : null);
        setNewTaskTitle('');
        setTaskKind(TASK_KIND_OPTIONS[0]);
        setAuthor(null);
        setAssignee(null);
        setObservers([]);
        setTeam(null);
        setPriority(null);
        setTerminalNumber('');
        setEstimatedResolutionDate('');
        setErrors({});

        setItTpFields({
            serviceType: '',
            address: '',
            comment: '',
            problemDescription: '',
            contactPhone: '',
            terminalPaymentBlocked: false,
        });

        setDesignFields({
            taskType: '',
            layoutRequired: '',
            layoutFormat: '',
            visualizationRequired: '',
            address: '',
            vkLink: '',
            comment: '',
            timeWork: '',
            linkRecord: '',
        });

        setOkkFields({
            text: '',
            imageUrl: '',
            imageName: '',
        });
        setIsUploadingOkkImage(false);
    };

    const validateFields = () => {
        const nextErrors = {};

        if (requiresCreationRoleChoice && !selectedCreationRoleValue) nextErrors.creationRole = 'Выберите роль создания';
        if (!newTaskTitle.trim()) nextErrors.title = 'Название задачи обязательно';
        if (!shouldHideAuthor && !author) nextErrors.author = 'Автор задачи обязателен';
        if (!assignee) nextErrors.assignee = 'Исполнитель задачи обязателен';
        if (!fixedTeamCode && !team) nextErrors.team = 'Команда обязательна';
        if (!priority) nextErrors.priority = 'Важность задачи обязательна';

        if (isItTp) {
            if (!itTpFields.serviceType.trim()) nextErrors.serviceType = 'Сервис обязателен';
            if (!itTpFields.address.trim()) nextErrors.itAddress = 'Адрес обязателен';
            if (!itTpFields.problemDescription.trim()) nextErrors.problemDescription = 'Описание проблемы обязательно';
        }

        if (isDesign) {
            if (!designFields.taskType.trim()) nextErrors.taskType = 'Вид рекламного оформления обязателен';
            if (!designFields.layoutRequired.trim()) nextErrors.layoutRequired = 'Какой нужен макет обязательно';
            if (!designFields.layoutFormat.trim()) nextErrors.layoutFormat = 'Формат макета обязателен';
            if (!designFields.visualizationRequired.trim()) nextErrors.visualizationRequired = 'Нужна визуализация обязательно';
            if (!designFields.address.trim()) nextErrors.designAddress = 'Адрес обязателен';
        }

        if (isOkk) {
            if (!okkFields.text.trim()) nextErrors.okkText = 'Текст обязателен';
            if (!okkFields.imageUrl.trim()) nextErrors.okkImage = 'Картинка обязательна';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleCreateTask = () => {
        if (!validateFields()) return;

        const resolvedTeamOption = team || lockedTeamOption;
        const commonTask = {
            title: newTaskTitle.trim(),
            taskKind: taskKind?.value || 'common',
            ...(author ? { author: author.value } : {}),
            assignee: assignee.value,
            observers: observers.map((o) => o.value),
            priority: priority.value,
            terminalNumber: terminalNumber.trim(),
            estimatedResolutionDate: estimatedResolutionDate || null,
        };

        const newTask = isOkk
            ? {
                ...commonTask,
                endpoint: '/api/tasks/okk',
                teamCode: TEAM_OKK_CODE,
                text: okkFields.text.trim(),
                imageUrl: okkFields.imageUrl,
            }
            : {
                ...commonTask,
                endpoint: '/setTask',
                team: resolvedTeamOption ? resolvedTeamOption.value : null,
                terminalPaymentBlocked: Boolean(itTpFields.terminalPaymentBlocked),
                description: descriptionJsonString,
            };

        onCreateTask(newTask);
        resetForm();
        onRequestClose();
    };

    const handleTeamChange = (selected) => {
        setTeam(selected);
        setAssignee(null);
    };

    const handleOkkImageUpload = async (event) => {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        if (!file) {
            setOkkFields((prev) => ({ ...prev, imageUrl: '', imageName: '' }));
            return;
        }

        if (!String(file.type || '').startsWith('image/')) {
            setErrors((prev) => ({ ...prev, okkImage: 'Можно загрузить только изображение' }));
            return;
        }

        try {
            await uploadOkkImageFile(file);
        } catch (error) {
            console.error('Ошибка при загрузке OKK изображения:', error);
            setErrors((prev) => ({ ...prev, okkImage: 'Не удалось загрузить изображение' }));
        }
    };

    const uploadOkkImageFile = async (inputFile) => {
        const file = ensureNamedImageFile(inputFile);
        if (!file) {
            throw new Error('Файл изображения не найден');
        }

        setIsUploadingOkkImage(true);
        setErrors((prev) => ({ ...prev, okkImage: undefined }));

        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/uploadFile', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (!response.data?.url) {
                throw new Error('Пустой ответ загрузки');
            }

            setOkkFields((prev) => ({
                ...prev,
                imageUrl: response.data.url,
                imageName: file.name,
            }));
        } finally {
            setIsUploadingOkkImage(false);
        }
    };

    const handleOkkClipboardPaste = async (event) => {
        const clipboardItems = Array.from(event.clipboardData?.items || []);
        const clipboardImage = clipboardItems
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .find((file) => file && String(file.type || '').startsWith('image/'));

        try {
            event.preventDefault();

            if (clipboardImage) {
                await uploadOkkImageFile(ensureNamedImageFile(clipboardImage, 'clipboard-image'));
                return;
            }

            const html = event.clipboardData?.getData('text/html') || '';
            const text = event.clipboardData?.getData('text/plain') || '';
            const tableImageFile = await clipboardPayloadToTableImageFile({ html, text });

            if (!tableImageFile) {
                setErrors((prev) => ({
                    ...prev,
                    okkImage: 'Не удалось распознать таблицу из буфера. Скопируйте ячейки Google Sheets и вставьте их сюда.',
                }));
                return;
            }

            await uploadOkkImageFile(tableImageFile);
        } catch (error) {
            console.error('Ошибка при вставке таблицы OKK:', error);
            setErrors((prev) => ({
                ...prev,
                okkImage: 'Не удалось превратить таблицу в изображение',
            }));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => {
                resetForm();
                onRequestClose();
            }}
            contentLabel="Создание новой задачи"
            className={styles.modalContent}
            overlayClassName="appModalOverlay"
        >
            <h2>Создать новую задачу</h2>

            {creationRoleOptions.length > 1 && (
                <div className={styles.field}>
                    <label>Создать от роли</label>
                    <Select
                        options={creationRoleOptions}
                        value={creationRole}
                        onChange={setCreationRole}
                        placeholder="Выберите роль"
                        isSearchable={false}
                        className={errors.creationRole ? styles.errorSelect : ''}
                    />
                    {errors.creationRole && <span className={styles.errorMessage}>{errors.creationRole}</span>}
                </div>
            )}

            {shouldWaitForRoleSelection ? (
                <div className={styles.field}>
                    <span className={styles.fileHint}>Сначала выберите роль, от имени которой нужно создать задачу.</span>
                </div>
            ) : (
                <>
                    <div className={styles.field}>
                        <label>Название задачи</label>
                        <input
                            type="text"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            placeholder="Введите название задачи"
                            className={errors.title ? styles.errorInput : ''}
                        />
                        {errors.title && <span className={styles.errorMessage}>{errors.title}</span>}
                    </div>

                    <div className={styles.field}>
                        <label>Тип задачи</label>
                        <Select
                            options={TASK_KIND_OPTIONS}
                            value={taskKind}
                            onChange={(selected) => setTaskKind(selected || TASK_KIND_OPTIONS[0])}
                            placeholder="Выберите тип задачи"
                            isSearchable={false}
                        />
                    </div>

                    {!shouldHideAuthor && (
                        <div className={styles.field}>
                            <label>Автор задачи</label>
                            <Select
                                options={usersOptionsAuthors}
                                value={author}
                                onChange={setAuthor}
                                placeholder="Выберите автора"
                                isSearchable
                                className={errors.author ? styles.errorSelect : ''}
                            />
                            {errors.author && <span className={styles.errorMessage}>{errors.author}</span>}
                        </div>
                    )}

                    <div className={styles.field}>
                        <label>Исполнитель задачи</label>
                        <Select
                            options={performerOptions}
                            value={assignee}
                            onChange={setAssignee}
                            placeholder="Выберите исполнителя"
                            isSearchable
                            className={errors.assignee ? styles.errorSelect : ''}
                        />
                        {errors.assignee && <span className={styles.errorMessage}>{errors.assignee}</span>}
                    </div>

                    {fixedTeamCode ? (
                        <div className={styles.field}>
                            <label>Команда</label>
                            <input
                                type="text"
                                value={lockedTeamOption?.label || FIXED_TEAM_LABEL_BY_CODE[fixedTeamCode] || fixedTeamCode}
                                readOnly
                            />
                        </div>
                    ) : (
                        <div className={styles.field}>
                            <label>Команда</label>
                            <Select
                                options={teamsOptions}
                                value={team}
                                onChange={handleTeamChange}
                                placeholder="Выберите команду"
                                isSearchable
                                className={errors.team ? styles.errorSelect : ''}
                            />
                            {errors.team && <span className={styles.errorMessage}>{errors.team}</span>}
                        </div>
                    )}

                    <div className={styles.field}>
                        <label>Наблюдатели</label>
                        <Select
                            options={usersOptionsObserver}
                            value={observers}
                            onChange={(selected) => setObservers(selected || [])}
                            placeholder="Выберите наблюдателей"
                            isMulti
                            isSearchable
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Номер терминала</label>
                        <input
                            type="text"
                            value={terminalNumber}
                            onChange={(e) => setTerminalNumber(e.target.value)}
                            placeholder="Например: T-1023"
                        />
                    </div>

                    <div className={styles.field}>
                        <label>Примерная дата решения</label>
                        <input
                            type="date"
                            value={estimatedResolutionDate}
                            onChange={(e) => setEstimatedResolutionDate(e.target.value)}
                        />
                    </div>

                    {isItTp && (
                        <>
                            <div className={styles.field}>
                                <label>Сервис</label>
                                <input
                                    type="text"
                                    value={itTpFields.serviceType}
                                    onChange={(e) => setItTpFields((p) => ({ ...p, serviceType: e.target.value }))}
                                    placeholder="Например: Терминал"
                                    className={errors.serviceType ? styles.errorInput : ''}
                                />
                                {errors.serviceType && <span className={styles.errorMessage}>{errors.serviceType}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Адрес</label>
                                <input
                                    type="text"
                                    value={itTpFields.address}
                                    onChange={(e) => setItTpFields((p) => ({ ...p, address: e.target.value }))}
                                    placeholder="Город, улица, дом"
                                    className={errors.itAddress ? styles.errorInput : ''}
                                />
                                {errors.itAddress && <span className={styles.errorMessage}>{errors.itAddress}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Комментарий</label>
                                <input
                                    type="text"
                                    value={itTpFields.comment}
                                    onChange={(e) => setItTpFields((p) => ({ ...p, comment: e.target.value }))}
                                    placeholder="Комментарий (необязательно)"
                                />
                            </div>

                            <div className={styles.field}>
                                <label>Телефон для связи</label>
                                <input
                                    type="tel"
                                    value={itTpFields.contactPhone}
                                    onChange={(e) => setItTpFields((p) => ({ ...p, contactPhone: e.target.value }))}
                                    placeholder="+7 999 123-45-67"
                                    inputMode="tel"
                                />
                                <span className={styles.fileHint}>
                                    По закрытию тикета или если возникнут вопросы, мы вам позвоним.
                                </span>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(itTpFields.terminalPaymentBlocked)}
                                        onChange={(e) => setItTpFields((p) => ({ ...p, terminalPaymentBlocked: e.target.checked }))}
                                    />
                                    <span className={styles.checkboxText}>
                                        <span>Терминал не принимает оплаты</span>
                                        <span className={styles.priorityNote}>(Эта задача будет взята в приоритет)</span>
                                    </span>
                                </label>
                            </div>

                            <div className={styles.field}>
                                <label>Описание проблемы</label>
                                <textarea
                                    value={itTpFields.problemDescription}
                                    onChange={(e) => setItTpFields((p) => ({ ...p, problemDescription: e.target.value }))}
                                    placeholder="Опишите проблему"
                                    rows={4}
                                    className={errors.problemDescription ? styles.errorTextarea : ''}
                                />
                                {errors.problemDescription && <span className={styles.errorMessage}>{errors.problemDescription}</span>}
                            </div>
                        </>
                    )}

                    {isDesign && (
                        <>
                            <div className={styles.field}>
                                <label>Вид рекламного оформления</label>
                                <input
                                    type="text"
                                    value={designFields.taskType}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, taskType: e.target.value }))}
                                    placeholder="Например: Листовка"
                                    className={errors.taskType ? styles.errorInput : ''}
                                />
                                {errors.taskType && <span className={styles.errorMessage}>{errors.taskType}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Какой нужен макет</label>
                                <input
                                    type="text"
                                    value={designFields.layoutRequired}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, layoutRequired: e.target.value }))}
                                    placeholder="Например: Флаер"
                                    className={errors.layoutRequired ? styles.errorInput : ''}
                                />
                                {errors.layoutRequired && <span className={styles.errorMessage}>{errors.layoutRequired}</span>}
                                <DesignLayoutPreview text={designFields.layoutRequired} />
                            </div>

                            <div className={styles.field}>
                                <label>Формат макета</label>
                                <input
                                    type="text"
                                    value={designFields.layoutFormat}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, layoutFormat: e.target.value }))}
                                    placeholder="Например: A6"
                                    className={errors.layoutFormat ? styles.errorInput : ''}
                                />
                                {errors.layoutFormat && <span className={styles.errorMessage}>{errors.layoutFormat}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Нужна визуализация</label>
                                <input
                                    type="text"
                                    value={designFields.visualizationRequired}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, visualizationRequired: e.target.value }))}
                                    placeholder="Да/Нет"
                                    className={errors.visualizationRequired ? styles.errorInput : ''}
                                />
                                {errors.visualizationRequired && <span className={styles.errorMessage}>{errors.visualizationRequired}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Адрес</label>
                                <input
                                    type="text"
                                    value={designFields.address}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, address: e.target.value }))}
                                    placeholder="Город, улица, дом"
                                    className={errors.designAddress ? styles.errorInput : ''}
                                />
                                {errors.designAddress && <span className={styles.errorMessage}>{errors.designAddress}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Ссылка на VK</label>
                                <input
                                    type="text"
                                    value={designFields.vkLink}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, vkLink: e.target.value }))}
                                    placeholder="https://vk.com/..."
                                />
                            </div>

                            <div className={styles.field}>
                                <label>Комментарий</label>
                                <textarea
                                    value={designFields.comment}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, comment: e.target.value }))}
                                    placeholder="Комментарий/пожелания"
                                    rows={4}
                                />
                            </div>

                            <div className={styles.field}>
                                <label>Время работы</label>
                                <input
                                    type="text"
                                    value={designFields.timeWork}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, timeWork: e.target.value }))}
                                    placeholder="Например: пн-вс 10:00-20:00"
                                />
                            </div>

                            <div className={styles.field}>
                                <label>Ссылка на запись</label>
                                <input
                                    type="text"
                                    value={designFields.linkRecord}
                                    onChange={(e) => setDesignFields((p) => ({ ...p, linkRecord: e.target.value }))}
                                    placeholder="https://..."
                                />
                            </div>
                        </>
                    )}

                    {isOkk && (
                        <>
                            <div className={styles.field}>
                                <label>Текст для Telegram</label>
                                <textarea
                                    value={okkFields.text}
                                    onChange={(e) => setOkkFields((p) => ({ ...p, text: e.target.value }))}
                                    placeholder="Введите текст, который должен уйти партнеру в Telegram"
                                    rows={5}
                                    className={errors.okkText ? styles.errorTextarea : ''}
                                />
                                {errors.okkText && <span className={styles.errorMessage}>{errors.okkText}</span>}
                            </div>

                            <div className={styles.field}>
                                <label>Картинка</label>
                                <input type="file" accept="image/*" onChange={handleOkkImageUpload} />
                                <div
                                    className={styles.clipboardPasteZone}
                                    contentEditable
                                    suppressContentEditableWarning
                                    role="textbox"
                                    aria-label="Вставка таблицы Google Sheets"
                                    data-placeholder="Нажмите сюда и вставьте Ctrl+V скопированные ячейки Google Sheets. Мы превратим их в картинку автоматически."
                                    onPaste={(event) => {
                                        void handleOkkClipboardPaste(event);
                                    }}
                                    onInput={(event) => {
                                        event.currentTarget.textContent = '';
                                    }}
                                />
                                {isUploadingOkkImage ? <span className={styles.fileHint}>Загружаем изображение...</span> : null}
                                {okkFields.imageUrl ? (
                                    <div className={styles.imagePreview}>
                                        <img src={okkFields.imageUrl} alt={okkFields.imageName || 'OKK'} />
                                        <a href={okkFields.imageUrl} target="_blank" rel="noreferrer">
                                            {okkFields.imageName || 'Открыть изображение'}
                                        </a>
                                    </div>
                                ) : null}
                                {errors.okkImage && <span className={styles.errorMessage}>{errors.okkImage}</span>}
                            </div>
                        </>
                    )}

                    {!isItTp && !isDesign && !isOkk && (
                        <div className={styles.field}>
                            <label>Описание (сформируется после выбора команды)</label>
                            <textarea value={descriptionJsonString} readOnly rows={3} />
                        </div>
                    )}

                    <div className={styles.field}>
                        <label>Важность задачи</label>
                        <Select
                            options={priorityOptions}
                            value={priority}
                            onChange={setPriority}
                            placeholder="Выберите важность"
                            className={errors.priority ? styles.errorSelect : ''}
                        />
                        {errors.priority && <span className={styles.errorMessage}>{errors.priority}</span>}
                    </div>
                </>
            )}

            <div className={styles.buttonsContainer}>
                <button onClick={handleCreateTask} disabled={isUploadingOkkImage}>Создать</button>
                <button
                    onClick={() => {
                        resetForm();
                        onRequestClose();
                    }}
                >
                    Отмена
                </button>
            </div>
        </Modal>
    );
};

export default TaskModal;
